import { useState, useEffect, useCallback, type FormEvent } from 'react';
import type { ExpenseCategoryRecord } from '../../services/expenseCategoryApi';
import {
    fetchExpenseCategories,
    createExpenseCategory,
    updateExpenseCategory,
    deleteExpenseCategory,
} from '../../services/expenseCategoryApi';
import type { ApiError } from '../../services/api';
import './ExpenseCategories.css';
import { useTranslation } from 'react-i18next';

type DrawerMode = 'closed' | 'create' | 'edit';

export default function ExpenseCategories() {
    const { t: _t } = useTranslation();
    const [categories, setCategories] = useState<ExpenseCategoryRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<ExpenseCategoryRecord | null>(null);
    const [saving, setSaving] = useState(false);

    /* Drawer state */
    const [drawerMode, setDrawerMode] = useState<DrawerMode>('closed');
    const [editId, setEditId] = useState<string | null>(null);
    const [formName, setFormName] = useState('');
    const [formDesc, setFormDesc] = useState('');
    const [formActive, setFormActive] = useState(true);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const loadCategories = useCallback(async () => {
        setLoading(true);
        setErrorMsg('');
        try {
            const data = await fetchExpenseCategories();
            setCategories(data);
        } catch {
            setErrorMsg('Failed to load categories.');
            setCategories([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    /* ── Drawer ── */
    const openCreate = () => {
        setDrawerMode('create');
        setEditId(null);
        setFormName(''); setFormDesc(''); setFormActive(true);
        setFormErrors({});
        setErrorMsg('');
    };

    const openEdit = (cat: ExpenseCategoryRecord) => {
        setDrawerMode('edit');
        setEditId(cat.id);
        setFormName(cat.name);
        setFormDesc(cat.description ?? '');
        setFormActive(cat.is_active);
        setFormErrors({});
        setErrorMsg('');
    };

    const closeDrawer = () => { setDrawerMode('closed'); setEditId(null); };

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!formName.trim()) errs.name = 'Category name is required.';
        if (formName.trim().length > 100) errs.name = 'Name must be 100 characters or less.';
        if (formDesc.trim().length > 255) errs.description = 'Description must be 255 characters or less.';
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (!validate()) return;

        const schoolId = localStorage.getItem('school_id');
        if (!schoolId) {
            setErrorMsg('No school selected. Please select a school first.');
            return;
        }

        setSaving(true);
        setErrorMsg('');
        try {
            if (drawerMode === 'create') {
                const created = await createExpenseCategory({
                    school_id: schoolId,
                    name: formName.trim(),
                    description: formDesc.trim() || null,
                    is_active: formActive,
                });
                setCategories((prev) => [created, ...prev]);
                showSuccess('Category created.');
            } else if (drawerMode === 'edit' && editId) {
                const updated = await updateExpenseCategory(editId, {
                    name: formName.trim(),
                    description: formDesc.trim() || null,
                    is_active: formActive,
                });
                setCategories((prev) => prev.map((c) => c.id === editId ? updated : c));
                showSuccess('Category updated.');
            }
            closeDrawer();
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const fieldErrors: Record<string, string> = {};
                for (const [key, msgs] of Object.entries(apiErr.errors)) {
                    fieldErrors[key] = msgs[0];
                }
                setFormErrors(fieldErrors);
            } else {
                setErrorMsg(apiErr.message || 'Failed to save category.');
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        setErrorMsg('');
        try {
            await deleteExpenseCategory(deleteTarget.id);
            setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
            setDeleteTarget(null);
            showSuccess('Category removed.');
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to delete category.');
            setDeleteTarget(null);
        } finally {
            setSaving(false);
        }
    };

    /* ── Filter ── */
    const filtered = categories.filter((c) => {
        const q = searchQuery.toLowerCase();
        if (q && !c.name.toLowerCase().includes(q) && !(c.description ?? '').toLowerCase().includes(q)) return false;
        return true;
    });

    const drawerOpen = drawerMode !== 'closed';

    return (
        <div className="ecat">
            {successMsg && (
                <div className="ecat__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="ecat__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {errorMsg && (
                <div className="ecat__error-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                </div>
            )}

            <div className="ecat__header">
                <div className="ecat__header-text">
                    <h1 className="ecat__title">Expense Categories</h1>
                    <p className="ecat__subtitle">Manage spending categories for your organization.</p>
                </div>
                <button className="ecat__add-btn" onClick={openCreate}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    New Category
                </button>
            </div>

            {/* Search */}
            <div className="ecat__search-wrap">
                <svg className="ecat__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                    className="ecat__search-input"
                    type="text"
                    placeholder="Search categories…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* Grid */}
            {loading ? (
                <div className="ecat__loading"><div className="ecat__loader"><span className="ecat__loader-dot" /><span className="ecat__loader-dot" /><span className="ecat__loader-dot" /></div></div>
            ) : filtered.length === 0 ? (
                <div className="ecat__empty">
                    <h3 className="ecat__empty-title">No categories found</h3>
                    <p className="ecat__empty-text">Create a new category to get started.</p>
                </div>
            ) : (
                <div className="ecat__table-wrap">
                    <table className="ecat__table">
                        <thead>
                            <tr>
                                <th className="ecat__th">Category Name</th>
                                <th className="ecat__th">Description</th>
                                <th className="ecat__th">Status</th>
                                <th className="ecat__th ecat__th--actions"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr className="ecat__row" key={c.id}>
                                    <td className="ecat__td ecat__td--name">{c.name}</td>
                                    <td className="ecat__td ecat__td--desc">{c.description ?? '—'}</td>
                                    <td className="ecat__td">
                                        <span className={`ecat__badge ${c.is_active ? 'ecat__badge--active' : 'ecat__badge--inactive'}`}>
                                            {c.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="ecat__td ecat__td--actions">
                                        <button className="ecat__row-btn" title="Edit" onClick={() => openEdit(c)}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                        </button>
                                        <button className="ecat__row-btn ecat__row-btn--delete" title="Delete" onClick={() => setDeleteTarget(c)}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Drawer */}
            {drawerOpen && <div className="ecat__drawer-backdrop" onClick={closeDrawer} />}
            <div className={`ecat__drawer ${drawerOpen ? 'ecat__drawer--open' : ''}`}>
                <form className="ecat__drawer-inner" onSubmit={handleSubmit}>
                    <div className="ecat__drawer-header">
                        <h2 className="ecat__drawer-title">{drawerMode === 'create' ? 'New Category' : 'Edit Category'}</h2>
                        <button className="ecat__drawer-close" type="button" onClick={closeDrawer}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>

                    <div className="ecat__drawer-body">
                        <div className="ecat__drawer-card">
                            <div className="ecat__field">
                                <label className="ecat__label">Category Name *</label>
                                <input className={`ecat__input ${formErrors.name ? 'ecat__input--error' : ''}`} type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g. Utilities" maxLength={100} />
                                {formErrors.name && <p className="ecat__error">{formErrors.name}</p>}
                            </div>

                            <div className="ecat__field">
                                <label className="ecat__label">Description</label>
                                <textarea className={`ecat__textarea ${formErrors.description ? 'ecat__input--error' : ''}`} value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Brief description…" rows={3} maxLength={255} />
                                {formErrors.description && <p className="ecat__error">{formErrors.description}</p>}
                            </div>

                            <div className="ecat__field ecat__field--toggle">
                                <label className="ecat__label">Active</label>
                                <button
                                    type="button"
                                    className={`ecat__toggle ${formActive ? 'ecat__toggle--on' : ''}`}
                                    onClick={() => setFormActive(!formActive)}
                                >
                                    <span className="ecat__toggle-knob" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="ecat__drawer-footer">
                        <button className="ecat__btn-cancel" type="button" onClick={closeDrawer}>Cancel</button>
                        <button className="ecat__btn-save" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : (drawerMode === 'create' ? 'Create Category' : 'Update Category')}
                        </button>
                    </div>
                </form>
            </div>

            {/* Delete confirm */}
            {deleteTarget && (
                <div className="ecat__overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="ecat__modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="ecat__modal-title">Remove Category</h3>
                        <p className="ecat__modal-text">
                            Remove <strong>{deleteTarget.name}</strong>? This action can be undone by an administrator.
                        </p>
                        <div className="ecat__modal-actions">
                            <button className="ecat__btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="ecat__btn-delete" onClick={confirmDelete} disabled={saving}>
                                {saving ? 'Removing…' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
